/**
 * Outbox v2 storage — WEB engine (IndexedDB). Contract: `outboxStore.ts`.
 *
 * Layout: database `atlas-outbox-v2`, object store `entries` keyed by `seq` (autoIncrement), with
 * an index `byOrgSeq` on `[orgFirestoreId, seq]` for the per-organization FIFO and a UNIQUE index
 * `byOrgEntryId` on `[orgFirestoreId, entryId]`, so a duplicate entry is refused by the database
 * itself rather than by a check-then-write in JS. A second object store, `meta`, holds the install
 * id (key `installId`); it is not part of any queue listing.
 *
 * WHY IndexedDB and not the v1 outbox's localStorage: localStorage is ~5 MB shared with the whole
 * origin (v1 caps itself at 2 MB and then DROPS), and web sales today live only in Firestore's
 * memory queue and die with the tab (stage-0 defect 2). IndexedDB survives the tab and is bounded
 * by the browser's origin quota.
 *
 * WHY the raw API and not `idb`: `idb` 7.1.1 is in the bundle only as a transitive dependency of
 * `@firebase/app`; importing it here would be an undeclared dependency whose version a Firebase
 * upgrade could change underneath the queue. The few request shapes used below do not need it.
 *
 * DURABILITY. Every mutation is ONE readwrite transaction for which `durability: 'strict'` is
 * REQUESTED (a browser may treat it as a hint), and its promise resolves on the transaction's
 * `complete` event — not on the request's `success`, which fires before the commit. `appendDelete`
 * adds the delete and purges its superseded entries in the same transaction, so on web it is
 * all-or-nothing, and an append is never indeterminate here: a transaction either completes or
 * aborts, and the promise reports which.
 *
 * OPENING. A database that cannot be opened is a loud `OutboxStorageUnavailableError`, never a
 * hang: `blocked` (another tab holds an older version open and will not let go), `version` (a
 * NEWER bundle already upgraded this database, e.g. after an OTA rollback), `open-failed` (any other
 * refusal, such as storage disabled).
 *
 * MULTIPLE TABS. `seq` comes from the object store's key generator, which is monotonic for the
 * database across every connection, so two tabs appending concurrently get distinct, ordered seqs.
 * Choosing WHICH tab drains is the queue's job (Web Locks, P3), not this layer's.
 */
import {
  applyEntryPatch,
  isStorageSafeId,
  prepareNewEntry,
  validateStoredEntry,
  type NewOutboxEntry,
  type OutboxEntryPatch,
  type OutboxEntryV2,
} from './outboxEntry'
import {
  checkAdoptionAudit,
  createSharedStoreOpener,
  generateInstallId,
  OutboxCorruptEntryError,
  OutboxDuplicateEntryError,
  OutboxStorageUnavailableError,
  OutboxValidationError,
  planDeletePurge,
  type AdoptForeignResult,
  type AppendDeleteResult,
  type OutboxCensus,
  type OutboxCorruptSlot,
  type OutboxSlot,
  type OutboxStore,
} from './outboxStore'

export const OUTBOX_DATABASE_NAME = 'atlas-outbox-v2'
/**
 * The Firestore-mode MIRROR-INTENT journal (ATL-1459, stage 3 P7; `global/Firebase/mirrorIntent.ts`):
 * the same engine in its own database, so the v2 master queue (P3) can never see a mirror whose
 * Firestore commit may not have happened.
 */
export const MIRROR_INTENT_DATABASE_NAME = 'atlas-mirror-intent-v1'
/** The QUARANTINE of v1 absolute stock mirrors (P8 review 1, S-3): its own database, never drained. */
export const QUARANTINE_DATABASE_NAME = 'atlas-outbox-v2-quarantine'
const DATABASE_VERSION = 1
const ENTRIES = 'entries'
const META = 'meta'
const INSTALL_ID_KEY = 'installId'
const BY_ORG_SEQ = 'byOrgSeq'
const BY_ORG_ENTRY_ID = 'byOrgEntryId'

export interface IndexedDbEnvironment {
  indexedDB: IDBFactory
  IDBKeyRange: typeof IDBKeyRange
}

export interface WebOutboxStoreOptions {
  env?: IndexedDbEnvironment
  databaseName?: string
  /** Injectable for tests. */
  generateId?: () => string
  /** Injectable for tests. */
  now?: () => number
}

function defaultEnvironment(): IndexedDbEnvironment {
  const g = globalThis as { indexedDB?: IDBFactory; IDBKeyRange?: typeof IDBKeyRange }
  if (!g.indexedDB || !g.IDBKeyRange) throw new Error('IndexedDB is unavailable in this browser')
  return { indexedDB: g.indexedDB, IDBKeyRange: g.IDBKeyRange }
}

function describe(value: unknown): string | null {
  try {
    return JSON.stringify(value) ?? null
  } catch {
    return null
  }
}

function corruptSlot(primaryKey: IDBValidKey, row: unknown, reason: string): OutboxCorruptSlot {
  const record = row && typeof row === 'object' ? (row as Record<string, unknown>) : {}
  return {
    status: 'corrupt',
    seq: typeof primaryKey === 'number' ? primaryKey : null,
    entryId: typeof record.entryId === 'string' ? record.entryId : null,
    ref: String(primaryKey),
    reason: `unreadable: ${reason}`,
    raw: describe(row),
  }
}

/** How a transaction body reports its outcome; the promise settles only when the transaction does. */
interface Finisher<T> {
  resolve(value: T): void
  fail(error: unknown): void
}

type ReadRow =
  | { ok: true; key: IDBValidKey; entry: OutboxEntryV2 }
  | { ok: false; key: IDBValidKey; slot: OutboxCorruptSlot }

/*
 * The engine's only IndexedDB writes. Gathered into three top-level functions so the W-writer census
 * (scripts/wWriterCensus.ts) holds each to one reviewed `NON_FIRESTORE_RECEIVERS` entry: their
 * receiver is an `IDBObjectStore`, and a member call named `add`/`put`/`delete` on an unproven
 * receiver is otherwise refused as a possible Firestore write.
 */
function idbAdd(objectStore: IDBObjectStore, value: unknown): IDBRequest<IDBValidKey> {
  return objectStore.add(value)
}

function idbPut(objectStore: IDBObjectStore, value: unknown, key?: IDBValidKey): IDBRequest<IDBValidKey> {
  return key === undefined ? objectStore.put(value) : objectStore.put(value, key)
}

function idbDelete(objectStore: IDBObjectStore, key: IDBValidKey): IDBRequest<undefined> {
  return objectStore.delete(key)
}

function readRow(key: IDBValidKey, value: unknown): ReadRow {
  const validated = validateStoredEntry(value)
  if (validated.ok && validated.value.seq === key) return { ok: true, key, entry: validated.value }
  return { ok: false, key, slot: corruptSlot(key, value, validated.ok ? 'seq disagrees with its key' : validated.reason) }
}

export function createWebOutboxStore(options: WebOutboxStoreOptions = {}): OutboxStore {
  const env = options.env ?? defaultEnvironment()
  const databaseName = options.databaseName ?? OUTBOX_DATABASE_NAME
  const generateId = options.generateId ?? generateInstallId
  const now = options.now ?? Date.now
  let connection: Promise<IDBDatabase> | null = null
  let install: Promise<{ id: string; origin: 'existing' | 'created' }> | null = null
  let closed = false

  function open(): Promise<IDBDatabase> {
    if (closed) return Promise.reject(new Error('outbox store is closed'))
    if (connection) return connection
    const opening = new Promise<IDBDatabase>((resolve, reject) => {
      let settled = false
      const fail = (error: Error): void => {
        if (settled) return
        settled = true
        reject(error)
      }
      const request = env.indexedDB.open(databaseName, DATABASE_VERSION)
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(ENTRIES)) {
          const entries = db.createObjectStore(ENTRIES, { keyPath: 'seq', autoIncrement: true })
          entries.createIndex(BY_ORG_SEQ, ['orgFirestoreId', 'seq'])
          entries.createIndex(BY_ORG_ENTRY_ID, ['orgFirestoreId', 'entryId'], { unique: true })
        }
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META)
      }
      request.onblocked = () => {
        fail(new OutboxStorageUnavailableError('blocked', 'another tab holds an older version of the outbox database open'))
      }
      request.onsuccess = () => {
        const db = request.result
        if (settled) {
          // We already reported `blocked`; a late success must not leak an open connection.
          db.close()
          return
        }
        settled = true
        // Another tab upgrading the schema, or the browser closing the connection: let go of it
        // and reopen on the next operation rather than keep using a dead handle.
        db.onversionchange = () => {
          db.close()
          if (connection === opening) connection = null
        }
        db.onclose = () => {
          if (connection === opening) connection = null
        }
        resolve(db)
      }
      request.onerror = (event) => {
        event.preventDefault()
        const error = request.error
        fail(
          error?.name === 'VersionError'
            ? new OutboxStorageUnavailableError('version', `the database is newer than version ${DATABASE_VERSION}`)
            : new OutboxStorageUnavailableError('open-failed', error?.message ?? 'unknown error'),
        )
      }
    })
    connection = opening
    opening.catch(() => {
      if (connection === opening) connection = null
    })
    return opening
  }

  /**
   * Run `body` inside ONE transaction; resolve with the value it `resolve`d once the transaction
   * has COMMITTED, reject if it aborts. `body` must only chain IndexedDB requests — awaiting
   * anything else would let the transaction auto-commit half-way.
   */
  async function transact<T>(
    stores: string[],
    mode: IDBTransactionMode,
    body: (tx: IDBTransaction, finish: Finisher<T>) => void,
  ): Promise<T> {
    const db = await open()
    return new Promise<T>((resolve, reject) => {
      let tx: IDBTransaction
      try {
        tx = mode === 'readwrite' ? db.transaction(stores, mode, { durability: 'strict' }) : db.transaction(stores, mode)
      } catch (error) {
        reject(error)
        return
      }
      let result: { value: T } | null = null
      let failure: { error: unknown } | null = null
      const finish: Finisher<T> = {
        resolve(value) {
          result = { value }
        },
        fail(error) {
          if (!failure) failure = { error }
          try {
            tx.abort()
          } catch {
            // Already finishing; `onabort`/`oncomplete` below still settles the promise.
          }
        },
      }
      tx.oncomplete = () => {
        if (failure) reject(failure.error)
        else if (result) resolve(result.value)
        else reject(new Error('outbox transaction committed without a result'))
      }
      tx.onabort = () => reject(failure ? failure.error : (tx.error ?? new Error('outbox transaction aborted')))
      try {
        body(tx, finish)
      } catch (error) {
        finish.fail(error)
      }
    })
  }

  /** Read the install id, or mint and store one (one transaction, so two tabs agree on it). */
  function installId(): Promise<{ id: string; origin: 'existing' | 'created' }> {
    if (install) return install
    const loading = transact<{ id: string; origin: 'existing' | 'created' }>([META], 'readwrite', (tx, finish) => {
      const meta = tx.objectStore(META)
      const request = meta.get(INSTALL_ID_KEY)
      request.onsuccess = () => {
        const stored: unknown = request.result
        if (isStorageSafeId(stored)) {
          finish.resolve({ id: stored, origin: 'existing' })
          return
        }
        const id = generateId()
        if (!isStorageSafeId(id)) {
          finish.fail(new Error('generated install id is not storage-safe'))
          return
        }
        const put = idbPut(meta, id, INSTALL_ID_KEY)
        put.onsuccess = () => finish.resolve({ id, origin: 'created' })
        put.onerror = () => finish.fail(put.error)
      }
      request.onerror = () => finish.fail(request.error)
    })
    install = loading
    loading.catch(() => {
      if (install === loading) install = null
    })
    return loading
  }

  function orgRange(orgFirestoreId: string): IDBKeyRange {
    return env.IDBKeyRange.bound([orgFirestoreId, -Infinity], [orgFirestoreId, Infinity])
  }

  function assertOrgId(orgFirestoreId: string): void {
    if (!isStorageSafeId(orgFirestoreId)) throw new OutboxValidationError('orgFirestoreId: not a storage-safe id')
  }

  function okSlot(entry: OutboxEntryV2, currentInstall: string): OutboxSlot {
    return { status: 'ok', seq: entry.seq, entry, foreignInstall: entry.installId !== currentInstall }
  }

  /**
   * `add` the prepared entry; `then` receives it with its seq. The unique index refusing it is a
   * duplicate — classified by reading the row that holds the id (the refusal is `preventDefault`ed,
   * so the transaction stays alive for that read, then aborts).
   */
  function addEntry(
    entries: IDBObjectStore,
    prepared: Omit<OutboxEntryV2, 'seq'>,
    finish: Finisher<unknown>,
    then: (entry: OutboxEntryV2) => void,
  ): void {
    const request = idbAdd(entries, prepared)
    request.onsuccess = () => {
      const seq = request.result
      if (typeof seq !== 'number') {
        finish.fail(new Error('outbox key generator returned a non-numeric seq'))
        return
      }
      then({ ...prepared, seq })
    }
    request.onerror = (event) => {
      event.preventDefault()
      if (request.error?.name !== 'ConstraintError') {
        finish.fail(request.error ?? new Error('outbox append failed'))
        return
      }
      const holder = entries.index(BY_ORG_ENTRY_ID).openCursor([prepared.orgFirestoreId, prepared.entryId])
      holder.onsuccess = () => {
        const cursor = holder.result
        const existing = cursor && readRow(cursor.primaryKey, cursor.value).ok ? 'ok' : 'corrupt'
        finish.fail(new OutboxDuplicateEntryError(prepared.orgFirestoreId, prepared.entryId, existing))
      }
      holder.onerror = () => finish.fail(holder.error)
    }
  }

  async function prepare(input: NewOutboxEntry): Promise<Omit<OutboxEntryV2, 'seq'>> {
    const prepared = prepareNewEntry(input)
    if (!prepared.ok) throw new OutboxValidationError(prepared.reason)
    return { ...prepared.value, installId: (await installId()).id, adoptions: [] }
  }

  /** Every row of one organization, in seq order, read inside `tx`. */
  function readOrg(
    entries: IDBObjectStore,
    orgFirestoreId: string,
    finish: Finisher<unknown>,
    then: (rows: ReadRow[]) => void,
  ): void {
    const rows: ReadRow[] = []
    const request = entries.index(BY_ORG_SEQ).openCursor(orgRange(orgFirestoreId))
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) {
        then(rows)
        return
      }
      rows.push(readRow(cursor.primaryKey, cursor.value))
      cursor.continue()
    }
    request.onerror = () => finish.fail(request.error)
  }

  /** Look an entry up by id inside a transaction; `then` receives its key and validated value. */
  function findEntry(
    entries: IDBObjectStore,
    orgFirestoreId: string,
    entryId: string,
    finish: Finisher<unknown>,
    then: (found: { key: IDBValidKey; entry: OutboxEntryV2 } | null) => void,
  ): void {
    const request = entries.index(BY_ORG_ENTRY_ID).openCursor([orgFirestoreId, entryId])
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) {
        then(null)
        return
      }
      const row = readRow(cursor.primaryKey, cursor.value)
      if (!row.ok) {
        finish.fail(new OutboxCorruptEntryError(orgFirestoreId, entryId, row.slot.reason))
        return
      }
      then({ key: row.key, entry: row.entry })
    }
    request.onerror = () => finish.fail(request.error)
  }

  const store: OutboxStore = {
    engine: 'indexeddb',

    async append(input) {
      const prepared = await prepare(input)
      return transact<OutboxEntryV2>([ENTRIES], 'readwrite', (tx, finish) => {
        addEntry(tx.objectStore(ENTRIES), prepared, finish as Finisher<unknown>, (entry) => finish.resolve(entry))
      })
    },

    async appendDelete(input) {
      if ((input as { kind?: unknown } | null)?.kind !== 'delete') {
        throw new OutboxValidationError('appendDelete: kind must be "delete"')
      }
      const prepared = await prepare(input)
      const current = (await installId()).id
      return transact<AppendDeleteResult>([ENTRIES], 'readwrite', (tx, finish) => {
        const entries = tx.objectStore(ENTRIES)
        addEntry(entries, prepared, finish as Finisher<unknown>, (entry) => {
          readOrg(entries, entry.orgFirestoreId, finish as Finisher<unknown>, (rows) => {
            const others = rows.filter((row) => row.key !== entry.seq)
            const plan = planDeletePurge(
              entry,
              others.map((row) => (row.ok ? okSlot(row.entry, current) : row.slot)),
            )
            const doomed = new Set(plan.purge.map((e) => e.entryId))
            const purged: OutboxEntryV2[] = []
            for (const row of others) {
              if (!row.ok || !doomed.has(row.entry.entryId)) continue
              idbDelete(entries, row.key)
              purged.push(row.entry)
            }
            // Resolved at `complete`: if any delete fails, the whole transaction — the delete
            // entry included — aborts.
            finish.resolve({ entry, purged, purgeWithheld: plan.withheld, purgeIncomplete: false })
          })
        })
      })
    },

    async list(orgFirestoreId) {
      assertOrgId(orgFirestoreId)
      const current = (await installId()).id
      return transact<OutboxSlot[]>([ENTRIES], 'readonly', (tx, finish) => {
        readOrg(tx.objectStore(ENTRIES), orgFirestoreId, finish as Finisher<unknown>, (rows) =>
          finish.resolve(rows.map((row) => (row.ok ? okSlot(row.entry, current) : row.slot))),
        )
      })
    },

    async get(orgFirestoreId, entryId) {
      assertOrgId(orgFirestoreId)
      return transact<OutboxEntryV2 | null>([ENTRIES], 'readonly', (tx, finish) => {
        findEntry(tx.objectStore(ENTRIES), orgFirestoreId, entryId, finish as Finisher<unknown>, (found) =>
          finish.resolve(found ? found.entry : null),
        )
      })
    },

    async update(orgFirestoreId, entryId, patch: OutboxEntryPatch) {
      assertOrgId(orgFirestoreId)
      return transact<OutboxEntryV2 | null>([ENTRIES], 'readwrite', (tx, finish) => {
        const entries = tx.objectStore(ENTRIES)
        findEntry(entries, orgFirestoreId, entryId, finish as Finisher<unknown>, (found) => {
          if (!found) {
            finish.resolve(null) // never resurrect a removed entry
            return
          }
          const merged = applyEntryPatch(found.entry, patch)
          if (!merged.ok) {
            finish.fail(new OutboxValidationError(merged.reason))
            return
          }
          const request = idbPut(entries, merged.value)
          request.onsuccess = () => finish.resolve(merged.value)
          request.onerror = () => finish.fail(request.error)
        })
      })
    },

    async remove(orgFirestoreId, entryId) {
      assertOrgId(orgFirestoreId)
      return transact<boolean>([ENTRIES], 'readwrite', (tx, finish) => {
        const entries = tx.objectStore(ENTRIES)
        findEntry(entries, orgFirestoreId, entryId, finish as Finisher<unknown>, (found) => {
          if (!found) {
            finish.resolve(false)
            return
          }
          const request = idbDelete(entries, found.key)
          request.onsuccess = () => finish.resolve(true)
          request.onerror = () => finish.fail(request.error)
        })
      })
    },

    async removeCorrupt(orgFirestoreId, ref) {
      const key = Number(ref)
      if (!Number.isSafeInteger(key) || String(key) !== ref) {
        throw new OutboxValidationError('removeCorrupt: ref is not a record key')
      }
      return transact<boolean>([ENTRIES], 'readwrite', (tx, finish) => {
        const entries = tx.objectStore(ENTRIES)
        const request = entries.get(key)
        request.onsuccess = () => {
          const row: unknown = request.result
          if (row === undefined) {
            finish.resolve(false)
            return
          }
          if (readRow(key, row).ok) {
            finish.fail(new OutboxValidationError('removeCorrupt: the record is not corrupt; use remove'))
            return
          }
          const rowOrg = (row as { orgFirestoreId?: unknown } | null)?.orgFirestoreId
          const attributable = isStorageSafeId(rowOrg)
          if (orgFirestoreId === null ? attributable : rowOrg !== orgFirestoreId) {
            finish.resolve(false)
            return
          }
          const deletion = idbDelete(entries, key)
          deletion.onsuccess = () => finish.resolve(true)
          deletion.onerror = () => finish.fail(deletion.error)
        }
        request.onerror = () => finish.fail(request.error)
      })
    },

    async adoptForeign(orgFirestoreId, entryIds, audit) {
      assertOrgId(orgFirestoreId)
      const checkedAudit = checkAdoptionAudit(audit)
      const current = (await installId()).id
      const at = now()
      return transact<AdoptForeignResult>([ENTRIES], 'readwrite', (tx, finish) => {
        const entries = tx.objectStore(ENTRIES)
        const result: AdoptForeignResult = { adopted: [], notForeign: [], notFound: [], corrupt: [] }
        const next = (index: number): void => {
          if (index >= entryIds.length) {
            finish.resolve(result)
            return
          }
          const entryId = entryIds[index]
          const request = entries.index(BY_ORG_ENTRY_ID).openCursor([orgFirestoreId, entryId])
          request.onsuccess = () => {
            const cursor = request.result
            if (!cursor) {
              result.notFound.push(entryId)
              next(index + 1)
              return
            }
            const row = readRow(cursor.primaryKey, cursor.value)
            if (!row.ok) {
              result.corrupt.push(entryId)
              next(index + 1)
              return
            }
            if (row.entry.installId === current) {
              result.notForeign.push(entryId)
              next(index + 1)
              return
            }
            const adopted = validateStoredEntry({
              ...row.entry,
              installId: current,
              adoptions: [...row.entry.adoptions, { fromInstallId: row.entry.installId, at, ...checkedAudit }],
            })
            if (!adopted.ok) {
              finish.fail(new OutboxValidationError(adopted.reason))
              return
            }
            const put = idbPut(entries, adopted.value)
            put.onsuccess = () => {
              result.adopted.push(entryId)
              next(index + 1)
            }
            put.onerror = () => finish.fail(put.error)
          }
          request.onerror = () => finish.fail(request.error)
        }
        next(0)
      })
    },

    async census() {
      const { id, origin } = await installId()
      return transact<OutboxCensus>([ENTRIES], 'readonly', (tx, finish) => {
        const organizations = new Set<string>()
        const census: OutboxCensus = {
          installId: id,
          installIdOrigin: origin,
          organizations: [],
          unattributed: [],
          foreignInstallEntries: [],
        }
        const request = tx.objectStore(ENTRIES).openCursor()
        request.onsuccess = () => {
          const cursor = request.result
          if (!cursor) {
            census.organizations = [...organizations].sort()
            finish.resolve(census)
            return
          }
          const org = (cursor.value as { orgFirestoreId?: unknown } | null)?.orgFirestoreId
          if (isStorageSafeId(org)) {
            organizations.add(org)
            const row = readRow(cursor.primaryKey, cursor.value)
            if (row.ok && row.entry.installId !== id) {
              census.foreignInstallEntries.push({
                orgFirestoreId: org,
                entryId: row.entry.entryId,
                seq: row.entry.seq,
                installId: row.entry.installId,
              })
            }
          } else {
            census.unattributed.push(corruptSlot(cursor.primaryKey, cursor.value, 'no organization'))
          }
          cursor.continue()
        }
        request.onerror = () => finish.fail(request.error)
      })
    },

    async close() {
      closed = true
      const pending = connection
      connection = null
      if (pending) {
        const db = await pending.catch(() => null)
        db?.close()
      }
    },
  }

  return store
}

// ============================================================================
// Platform binding + persistence helper
// ============================================================================

const sharedStore = createSharedStoreOpener(() => createWebOutboxStore())
const sharedMirrorIntentStore = createSharedStoreOpener(() =>
  createWebOutboxStore({ databaseName: MIRROR_INTENT_DATABASE_NAME }),
)
const sharedQuarantineStore = createSharedStoreOpener(() => createWebOutboxStore({ databaseName: QUARANTINE_DATABASE_NAME }))

/**
 * The app's web outbox store. After `close()` the next call opens a fresh one, whose operations
 * wait for the old one to finish (reviews 1 N2, 2 item 1). Never keep the returned store across a
 * `close()`; call `openOutboxStore()` again.
 */
export function openOutboxStore(): OutboxStore {
  return sharedStore.open()
}

/**
 * The app's web MIRROR-INTENT store (ATL-1459): same engine and contract as `openOutboxStore`, its
 * own database (`MIRROR_INTENT_DATABASE_NAME`). Same rule: never keep it across a `close()`.
 */
export function openMirrorIntentStore(): OutboxStore {
  return sharedMirrorIntentStore.open()
}

/**
 * The app's web QUARANTINE store (P8 review 1, S-3): same engine and contract, its own database
 * (`QUARANTINE_DATABASE_NAME`), never drained. Same rule: never keep it across a `close()`.
 */
export function openQuarantineStore(): OutboxStore {
  return sharedQuarantineStore.open()
}

export type PersistentStorageResult = 'already-persisted' | 'granted' | 'denied' | 'unsupported' | 'error'

type StorageManagerLike = { persisted?: () => Promise<boolean>; persist?: () => Promise<boolean> }

/**
 * Ask the browser not to evict this origin's storage under pressure (plan §2, risk 9). A request,
 * not a guarantee: browsers grant it on their own heuristics. Reports; never throws.
 */
export async function requestPersistentStorage(
  storage: StorageManagerLike | undefined = (globalThis as { navigator?: { storage?: StorageManagerLike } }).navigator
    ?.storage,
): Promise<PersistentStorageResult> {
  if (!storage || typeof storage.persist !== 'function') return 'unsupported'
  try {
    if (typeof storage.persisted === 'function' && (await storage.persisted())) return 'already-persisted'
    return (await storage.persist()) ? 'granted' : 'denied'
  } catch {
    return 'error'
  }
}
